import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import Logo from '@/components/Logo';
import MagneticButton from '@/components/motion/MagneticButton';
import SplitText from '@/components/motion/SplitText';
import EditableText from '@/components/editable/EditableText';
import { useSiteEdit } from '@/contexts/SiteEditContext';

const Hero: React.FC = () => {
  const ref = useRef<HTMLElement>(null);
  const prefersReduced = useReducedMotion();
  const { get, canEdit, editMode } = useSiteEdit();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });

  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', prefersReduced ? '0%' : '30%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, prefersReduced ? 1 : 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', prefersReduced ? '0%' : '-15%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6, 1], [1, 0.6, 0]);
  const glowY = useTransform(scrollYProgress, [0, 1], ['0%', '60%']);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative min-h-screen w-full overflow-hidden bg-[#0E1B2C]">

      {/* Background image with parallax */}
      <motion.div
        className="absolute inset-0"
        style={{ y: bgY, scale: bgScale }}>

        <img
          src="https://static.lumi.new/1e/1e9d2f8073e23dd41b071429aede6f6b.png"
          alt=""
          className="w-full h-full object-cover opacity-80" />

        <div className="absolute inset-0 bg-gradient-to-b from-[#0E1B2C]/85 via-[#0E1B2C]/55 to-[#0E1B2C]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E1B2C]/90 via-transparent to-[#0E1B2C]/45" />
      </motion.div>

      {/* Decorative corner glow with parallax */}
      <motion.div
        className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(184,212,90,0.22), transparent 60%)',
          y: glowY
        }}
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }} />


      {/* Subtle grid lines */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
      style={{
        backgroundImage: 'linear-gradient(#E8DCC4 1px, transparent 1px), linear-gradient(90deg, #E8DCC4 1px, transparent 1px)',
        backgroundSize: '120px 120px'
      }} />


      <motion.div
        className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 pt-40 lg:pt-44 pb-24 min-h-screen flex flex-col"
        style={{ y: contentY, opacity: contentOpacity }}>

        {/* Top tagline row */}
        <motion.div
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}>

          <div className="flex items-center gap-5">
            <motion.div
              initial={{ rotate: -20, scale: 0.8, opacity: 0 }}
              animate={{ rotate: 0, scale: 1, opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>

              <Logo size={88} />
            </motion.div>
            <div>
              <motion.div
                className="tracking-luxe text-[#C8A24A] mb-2 text-2xl"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}>
                <EditableText contentKey="hero.kicker" section="Hero" label="Sur-titre — Association" as="span">
                  ASSOCIATION
                </EditableText>
              </motion.div>
              <h1 className="font-serif-display text-[34px] lg:text-[44px] leading-none text-[#E8DCC4]">
                {canEdit && editMode ? (
                  <EditableText contentKey="hero.headline" section="Hero" label="Nom — Centre de Transformation" as="span">
                    {get('hero.headline', 'Centre de Transformation')}
                  </EditableText>
                ) : (
                  <SplitText text={get('hero.headline', 'Centre de Transformation')} delay={0.3} stagger={0.04} />
                )}
              </h1>
              <motion.div
                className="mt-3 flex items-center gap-3 text-[11px] tracking-mid uppercase text-[#C9E26A]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.9 }}>

                {['Accompagner', 'Révéler', 'Transformer', 'Construire'].map((w, i) =>
                <React.Fragment key={w}>
                    <motion.span
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.9 + i * 0.08 }}>

                      <EditableText contentKey={`hero.tags1.${i}`} section="Hero" label={`Mot-clé 1.${i + 1}`} as="span">{w}</EditableText>
                    </motion.span>
                    {i < 3 && <span className="w-1 h-1 rounded-full bg-[#C9E26A]" />}
                  </React.Fragment>
                )}
              </motion.div>
            </div>
          </div>

          <motion.div
            className="hidden lg:flex flex-col items-end text-right"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}>

            <div className="text-[11px] tracking-luxe text-[#C8A24A] mb-2">
              <EditableText contentKey="hero.badge" section="Hero" label="Bandeau — Projet suisse innovant" as="span">
                UN PROJET SUISSE INNOVANT
              </EditableText>
            </div>
            <div className="flex items-center gap-2 text-[11px] tracking-mid uppercase text-[#C9BFA8]">
              <span className="inline-block w-4 h-2.5 bg-[#D52B1E] relative">
                <span className="absolute inset-0 flex items-center justify-center text-white text-[8px] leading-none">+</span>
              </span>
              <EditableText contentKey="hero.ancrage" section="Hero" label="Ancrage géographique" as="span">
                Suisse · Genève · Gryon · Narbonne
              </EditableText>
            </div>
          </motion.div>
        </motion.div>

        {/* Main hero copy */}
        <div className="mt-20 lg:mt-28 max-w-5xl">
          <motion.div
            className="text-[11px] tracking-luxe text-[#C9E26A] mb-6 flex items-center gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}>

            <motion.span
              className="inline-block h-px bg-[#C9E26A]"
              initial={{ width: 0 }}
              animate={{ width: 40 }}
              transition={{ duration: 1, delay: 1.1, ease: [0.22, 1, 0.36, 1] }} />

            <EditableText contentKey="hero.badge" section="Hero" label="Bandeau — Projet suisse innovant" as="span">
              UN PROJET SUISSE INNOVANT
            </EditableText>
          </motion.div>

          <h2 className="font-serif-display text-[44px] sm:text-[64px] lg:text-[92px] leading-[0.98] text-[#E8DCC4] text-balance">
            <span className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: '110%' }}
                animate={{ y: '0%' }}
                transition={{ duration: 1.1, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}>

                <EditableText contentKey="hero.title.line1" section="Hero" label="Titre principal — ligne 1" as="span">
                  Pour une génération
                </EditableText>
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: '110%' }}
                animate={{ y: '0%' }}
                transition={{ duration: 1.1, delay: 1.35, ease: [0.22, 1, 0.36, 1] }}>

                <EditableText contentKey="hero.title.line2" section="Hero" label="Titre principal — ligne 2" as="span">
                  en mouvement.
                </EditableText>
              </motion.span>
            </span>
          </h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}>

            <EditableText
              contentKey="hero.intro"
              section="Hero"
              label="Paragraphe d'introduction"
              multiline
              as="p"
              className="mt-8 max-w-2xl text-[16px] lg:text-[18px] leading-[1.7] text-[#C9BFA8] block">
              Nous accompagnons les jeunes, les adultes en transition et les professionnels dans l'acquisition de compétences essentielles pour créer, produire et entreprendre avec les outils de l'IA d'aujourd'hui.
            </EditableText>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.7, ease: [0.22, 1, 0.36, 1] }}>

            <EditableText
              contentKey="hero.subintro"
              section="Hero"
              label="Paragraphe secondaire"
              multiline
              as="p"
              className="mt-5 max-w-2xl text-[15px] lg:text-[16px] leading-[1.7] text-[#C9BFA8]/90 block">
              Le Centre de Transformation développe des espaces physiques, mobiles et immersifs capables d'accompagner une nouvelle génération vers les compétences, les outils et les modèles humains de demain.
            </EditableText>
          </motion.div>

          <motion.div
            className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] tracking-mid uppercase text-[#C9E26A]/85"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.85 }}>

            {['IA', 'Création', 'Transmission', 'Expérimentation', 'Impact réel'].map((w, i) =>
            <React.Fragment key={w}>
                <motion.span
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.85 + i * 0.07 }}>

                  <EditableText contentKey={`hero.tags2.${i}`} section="Hero" label={`Mot-clé 2.${i + 1}`} as="span">{w}</EditableText>
                </motion.span>
                {i < 4 && <span className="text-[#C8A24A]">·</span>}
              </React.Fragment>
            )}
          </motion.div>

          <motion.div
            className="mt-12 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.8 }}>

            <MagneticButton
              href="#vision"
              className="group relative inline-flex items-center gap-3 px-7 py-4 text-[12px] tracking-mid uppercase text-[#0E1B2C] bg-[#C9E26A] overflow-hidden transition-colors">

              <span className="relative z-10"><EditableText contentKey="hero.cta1" section="Hero" label="Bouton — Découvrir le projet" as="span">Découvrir le projet</EditableText></span>
              <span className="relative z-10 w-6 h-px bg-[#0E1B2C] group-hover:w-10 transition-all duration-500" />
              <motion.span
                className="absolute inset-0 bg-[#E8DCC4]"
                initial={{ y: '100%' }}
                whileHover={{ y: '0%' }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} />

            </MagneticButton>

            <MagneticButton
              href="#contact"
              className="group relative inline-flex items-center gap-3 px-7 py-4 text-[12px] tracking-mid uppercase text-[#E8DCC4] hairline-strong overflow-hidden transition-colors duration-300 hover:text-[#0E1B2C]">

              <span className="relative z-10"><EditableText contentKey="hero.cta2" section="Hero" label="Bouton — Devenir partenaire" as="span">Devenir partenaire</EditableText></span>
              <motion.span
                className="absolute inset-0 bg-[#C9E26A]"
                initial={{ scaleY: 0, originY: 1 }}
                whileHover={{ scaleY: 1, originY: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} />

            </MagneticButton>
          </motion.div>

          {/* Tags */}
          <motion.div
            className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3 text-[11px] tracking-mid uppercase text-[#C9BFA8]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 2 }}>

            {['IA', 'Médias', 'Contenus', 'Projets', "Compétences d'avenir"].map((t, i) =>
            <React.Fragment key={t}>
                <motion.span
                className={i === 0 ? 'text-[#C9E26A]' : ''}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 2 + i * 0.07 }}>

                  <EditableText contentKey={`hero.tags3.${i}`} section="Hero" label={`Mot-clé 3.${i + 1}`} as="span">{t}</EditableText>
                </motion.span>
                {i < 4 && <span className="w-1 h-1 rounded-full bg-[#C9BFA8]/40" />}
              </React.Fragment>
            )}
          </motion.div>
        </div>

        {/* Bottom scroll indicator */}
        <motion.div
          className="mt-auto pt-16 flex items-end justify-between"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2.4 }}>

          <div className="flex items-center gap-3">
            <motion.div
              className="w-px h-12 bg-gradient-to-b from-transparent via-[#C9E26A] to-transparent"
              animate={{ scaleY: [0.4, 1, 0.4], opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{ transformOrigin: 'center' }} />

            <div className="text-[10px] tracking-luxe text-[#C9BFA8]/70"><EditableText contentKey="hero.scroll" section="Hero" label="Indicateur — Défiler" as="span">DÉFILER</EditableText></div>
          </div>
          <div className="hidden md:block text-right">
            <div className="text-[10px] tracking-luxe text-[#C9BFA8]/60 mb-1"><EditableText contentKey="hero.pilotlabel" section="Hero" label="Étiquette — Conteneur pilote" as="span">CONTENEUR PILOTE</EditableText></div>
            <div className="text-[12px] text-[#E8DCC4]"><EditableText contentKey="hero.pilotdesc" section="Hero" label="Description — Conteneur pilote" as="span">Mobile · Intelligent · Connecté</EditableText></div>
          </div>
        </motion.div>
      </motion.div>
    </section>);

};

export default Hero;